/**
 * The lobby: rounds you can join, and the ones already running.
 *
 * Polled rather than socketed. Watching every listed round would mean a
 * subscription per row and a snapshot for each — real work on the server for a
 * screen where being three seconds stale costs nothing. The socket is for the
 * round you are actually playing.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client.ts';
import { errorMessage } from '../api/errors.ts';
import type { RoundView } from '../api/types.ts';
import { useT } from '../i18n/index.tsx';
import { etb, etbWhole, countdown, countdownHMS, daysUntil, weekdayIndex, roundRef, prizeFigure, winPatternLabel } from '../format.ts';
import type { BalanceView } from '../api/types.ts';

const POLL_MS = 4_000;

interface LobbyProps {
  balance: BalanceView | null;
  /** The house's support contact, when one is configured — null hides the link. */
  supportUrl: string | null;
  onOpenRound(roundId: string): void;
  onRefreshBalance(): void;
}

export function Lobby({ balance, supportUrl, onOpenRound, onRefreshBalance }: LobbyProps) {
  const t = useT();
  const [rounds, setRounds] = useState<RoundView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const { rounds: next } = await api.rounds();
      setRounds(next);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, t));
    }
  }, [t]);

  useEffect(() => {
    void load();
    const poll = window.setInterval(() => void load(), POLL_MS);
    // A separate, faster timer purely so the "closes in 1:23" countdowns tick
    // between polls — re-fetching once a second to move a clock would be waste.
    const clock = window.setInterval(() => setTick((n) => n + 1), 1_000);
    return () => {
      window.clearInterval(poll);
      window.clearInterval(clock);
    };
  }, [load]);

  useEffect(() => {
    onRefreshBalance();
  }, [onRefreshBalance]);

  return (
    <div className="screen">
      <header className="lobby__header">
        <h1 className="lobby__title">{t.lobbyTitle}</h1>
        {balance && (
          <div className="lobby__balance">
            <span className="lobby__balance-label">{t.available}</span>
            <strong className="lobby__balance-value">{etb(balance.available)}</strong>
          </div>
        )}
      </header>

      {error && <div className="banner banner--error">{error}</div>}

      {rounds === null && <div className="lobby__empty">{t.connecting}</div>}

      {rounds !== null && rounds.length === 0 && (
        <div className="lobby__empty">
          <p className="lobby__empty-title">{t.lobbyEmpty}</p>
          <p className="lobby__empty-hint">{t.lobbyEmptyHint}</p>
        </div>
      )}

      <ul className="roundlist">
        {(rounds ?? []).map((round) => (
          <RoundRow key={round.id} round={round} onOpen={() => onOpenRound(round.id)} />
        ))}
      </ul>

      {supportUrl && (
        <footer className="lobby__footer">
          <a className="lobby__support" href={supportUrl} target="_blank" rel="noreferrer">
            {t.contactSupport}
          </a>
        </footer>
      )}
    </div>
  );
}

function RoundRow({ round, onOpen }: { round: RoundView; onOpen: () => void }) {
  const t = useT();
  const isLive = round.status === 'live' || round.status === 'starting';
  const isArif = round.gameTypeSlug === 'arif';

  // Arif Bingo counts down to the scheduled start, not to when buying closes —
  // those differ by `join_closes_before_seconds`. Rounds opened before
  // migration 024 hold `startsAt` null but their `joinClosesAt` *is* the
  // scheduled start, so fall back to it. Whole days past the 24h mark,
  // `mm:ss` inside it.
  const target = isArif ? (round.startsAt ?? round.joinClosesAt) : round.joinClosesAt;
  const arifDays = isArif ? daysUntil(target) : null;
  const dayIdx = isArif ? weekdayIndex(target) : null;
  const arifDay = arifDays !== null && dayIdx !== null ? t.weekdays[dayIdx] : null;
  const patternText = winPatternLabel(round.patternName, t);
  const countdownText: string | null =
    arifDays !== null
      ? arifDay
        ? `${t.inDays(arifDays)} · ${arifDay}`
        : t.inDays(arifDays)
      : isArif
        ? countdownHMS(target)
        : countdown(round.joinClosesAt);

  return (
    <li>
      <button
        type="button"
        className={`roundcard${isArif ? ' roundcard--arif' : ''}`}
        onClick={onOpen}
      >
        <div className="roundcard__main">
          <div className="roundcard__top">
            <span className="roundcard__ref">
              {round.gameTypeName ? (
                <span className="roundcard__type">{round.gameTypeName}</span>
              ) : null}
              #{roundRef(round.id)}
            </span>
            {isLive ? (
              <span className="chip chip--live">{t.live}</span>
            ) : countdownText ? (
              <span className="chip">{isArif ? t.startsIn(countdownText) : t.closesIn(countdownText)}</span>
            ) : (
              <span className="chip">{t.startingSoon}</span>
            )}
          </div>

          <div className="roundcard__figures">
            <div className="figure">
              <span className="figure__label">{t.entryFee}</span>
              <span className="figure__value">{etb(round.entryFee)}</span>
            </div>
            <div className="figure">
              <span className="figure__label">{t.prize}</span>
              <span className="figure__value figure__value--accent">
                {etbWhole(prizeFigure(round))}
              </span>
            </div>
          </div>

          {patternText && <p className="roundcard__pattern">{patternText}</p>}

          <div className="roundcard__players">
            {round.status === 'waiting'
              ? t.waitingForPlayers(round.playerCount ?? 0, round.minPlayers)
              : isLive
                ? t.gameInProgress
                : t.players(round.playerCount ?? 0)}
          </div>
        </div>

        <span className="roundcard__action">{isLive ? t.watchRound : t.joinRound}</span>
      </button>
    </li>
  );
}

